import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { 
  Sparkles, 
  Send, 
  X, 
  Maximize2, 
  Minimize2,
  MessageSquare,
  Loader2,
  Bot,
  User,
  ChevronDown
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Role-specific AI context and prompts
const ROLE_CONFIG = {
  investor: {
    title: "Deal Coach",
    placeholder: "Ask about deals, ROI, strategy...",
    systemContext: "You are a real estate investment coach. Help analyze deals, calculate ROI, suggest strategies, and guide on acquisitions, rehabs, and exits.",
    suggestions: [
      "Analyze my pipeline health",
      "What's a good ARV for this market?",
      "Should I wholesale or flip this deal?",
      "How can I improve my CEI score?"
    ]
  },
  realtor: {
    title: "Listing Assistant",
    placeholder: "Ask about listings, comps, offers...",
    systemContext: "You are a real estate agent assistant. Help with listing analysis, comparable sales, offer strategies, and investor client communication.",
    suggestions: [
      "How should I price this listing?",
      "What comps should I pull?",
      "How do I attract investor buyers?",
      "Draft an offer summary"
    ]
  },
  lender: {
    title: "Loan Advisor",
    placeholder: "Ask about loans, risk, borrowers...",
    systemContext: "You are a private lending advisor. Help evaluate loan applications, assess borrower risk, analyze deal viability, and structure terms.",
    suggestions: [
      "Evaluate this borrower's track record",
      "What LTV should I offer?",
      "Is this deal worth funding?",
      "How do I mitigate risk on this loan?"
    ]
  },
  title: {
    title: "Closing Guide",
    placeholder: "Ask about closings, docs, timelines...",
    systemContext: "You are a title and closing expert. Help with closing timelines, document requirements, title issues, and transaction coordination.",
    suggestions: [
      "What docs are needed for closing?",
      "How to resolve a title cloud?",
      "Typical timeline for this transaction?",
      "Checklist for investor closings"
    ]
  },
  contractor: {
    title: "Project Advisor",
    placeholder: "Ask about bids, scope, timelines...",
    systemContext: "You are a construction project advisor. Help with scope estimation, bid optimization, timeline planning, and investor project requirements.",
    suggestions: [
      "How should I bid this rehab?",
      "What's a fair price for this scope?",
      "How to prioritize multiple jobs?",
      "Common rehab items investors need"
    ]
  }
};

export function AIAssistant({ 
  userRole = "investor", 
  variant = "floating", // "floating" | "inline" | "panel"
  dealContext = null,
  className = ""
}) {
  const [isOpen, setIsOpen] = useState(variant === "inline" || variant === "panel");
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const config = ROLE_CONFIG[userRole] || ROLE_CONFIG.investor;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (text = input) => {
    if (!text.trim() || loading) return;

    const userMessage = { role: "user", content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);
    setShowSuggestions(false);

    try {
      // Build context with deal info if available
      let contextMessage = config.systemContext;
      if (dealContext) {
        contextMessage += `\n\nCurrent deal context:\n${JSON.stringify(dealContext, null, 2)}`;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/ai/chat`,
        {
          message: text,
          context: contextMessage,
          role: userRole,
          history: messages.slice(-6) // Last 6 messages for context
        },
        { withCredentials: true }
      );

      const assistantMessage = {
        role: "assistant",
        content: response.data.response || response.data.message || "I couldn't process that request."
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error("AI chat error:", error);
      const errorMessage = {
        role: "assistant",
        content: "Sorry, I'm having trouble connecting. Please try again."
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setShowSuggestions(true);
  };

  // Floating button + panel variant
  if (variant === "floating") {
    return (
      <>
        {/* Floating Button */}
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className={`fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-transform z-50 ${className}`}
            data-testid="ai-assistant-toggle"
          >
            <Sparkles className="w-6 h-6 text-black" />
          </button>
        )}

        {/* Chat Panel */}
        {isOpen && (
          <div 
            className={`fixed ${isExpanded ? 'inset-4' : 'bottom-6 right-6 w-96 h-[500px]'} bg-[#0A0A0A] border border-white/[0.08] rounded-lg shadow-2xl flex flex-col z-50 transition-all duration-200`}
            data-testid="ai-assistant-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.04] bg-[#0F0F0F] rounded-t-lg">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-white">{config.title}</h3>
                  <p className="text-[10px] text-white/40">Powered by AI</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="p-2 hover:bg-white/[0.04] rounded transition-colors"
                >
                  {isExpanded ? (
                    <Minimize2 className="w-4 h-4 text-white/50" />
                  ) : (
                    <Maximize2 className="w-4 h-4 text-white/50" />
                  )}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/[0.04] rounded transition-colors"
                >
                  <X className="w-4 h-4 text-white/50" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && showSuggestions && (
                <div className="space-y-3">
                  <p className="text-sm text-white/50 text-center mb-4">
                    How can I help you today?
                  </p>
                  <div className="grid gap-2">
                    {config.suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => sendMessage(suggestion)}
                        className="text-left px-3 py-2 bg-white/[0.02] border border-white/[0.04] rounded-md text-sm text-white/70 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/20 hover:text-[#D4AF37] transition-all"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {msg.role === "assistant" && (
                    <div className="w-7 h-7 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-3.5 h-3.5 text-[#D4AF37]" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                      msg.role === "user"
                        ? "bg-[#D4AF37] text-black"
                        : "bg-white/[0.04] text-white/90"
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  {msg.role === "user" && (
                    <div className="w-7 h-7 rounded-full bg-white/[0.08] flex items-center justify-center flex-shrink-0">
                      <User className="w-3.5 h-3.5 text-white/70" />
                    </div>
                  )}
                </div>
              ))}

              {loading && (
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <Bot className="w-3.5 h-3.5 text-[#D4AF37]" />
                  </div>
                  <div className="bg-white/[0.04] px-3 py-2 rounded-lg">
                    <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-white/[0.04] bg-[#0F0F0F] rounded-b-lg">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={config.placeholder}
                  className="flex-1 bg-white/[0.02] border border-white/[0.04] rounded-md px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/30"
                  disabled={loading}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading}
                  className="w-9 h-9 bg-[#D4AF37] rounded-md flex items-center justify-center hover:bg-[#B8972E] transition-colors disabled:opacity-50"
                >
                  <Send className="w-4 h-4 text-black" />
                </button>
              </div>
              {messages.length > 0 && (
                <button
                  onClick={clearChat}
                  className="mt-2 text-[10px] text-white/40 hover:text-white/50"
                >
                  Clear conversation
                </button>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  // Inline variant (embedded in page)
  if (variant === "inline") {
    return (
      <div className={`bg-[#0A0A0A] border border-white/[0.04] rounded-md ${className}`}>
        {/* Compact Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.04]">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs font-medium text-white/70">{config.title}</span>
          </div>
          {messages.length > 0 && (
            <button onClick={clearChat} className="text-[10px] text-white/40 hover:text-white/50">
              Clear
            </button>
          )}
        </div>

        {/* Messages - Compact */}
        <div className="h-48 overflow-y-auto p-3 space-y-3">
          {messages.length === 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {config.suggestions.slice(0, 3).map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => sendMessage(suggestion)}
                  className="px-2 py-1 bg-white/[0.02] border border-white/[0.04] rounded text-[10px] text-white/50 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] hover:border-[#D4AF37]/20 transition-all"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div key={idx} className={`text-xs ${msg.role === "user" ? "text-white/50" : "text-white/90"}`}>
                <span className={msg.role === "user" ? "text-[#D4AF37]" : "text-white/40"}>
                  {msg.role === "user" ? "You: " : "AI: "}
                </span>
                {msg.content}
              </div>
            ))
          )}
          {loading && (
            <div className="flex items-center gap-2 text-xs text-white/40">
              <Loader2 className="w-3 h-3 animate-spin" />
              Thinking...
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input - Compact */}
        <div className="p-2 border-t border-white/[0.04]">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask..."
              className="flex-1 bg-white/[0.02] border border-white/[0.04] rounded px-2 py-1.5 text-xs text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/30"
              disabled={loading}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className="p-1.5 bg-[#D4AF37] rounded hover:bg-[#B8972E] transition-colors disabled:opacity-50"
            >
              <Send className="w-3 h-3 text-black" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Panel variant (sidebar-style)
  return (
    <div className={`bg-[#0A0A0A] border border-white/[0.04] rounded-md h-full flex flex-col ${className}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.04]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
            <Bot className="w-4 h-4 text-[#D4AF37]" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-white">{config.title}</h3>
            <p className="text-[10px] text-white/40">AI Assistant</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && showSuggestions && (
          <div className="space-y-2">
            {config.suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => sendMessage(suggestion)}
                className="w-full text-left px-3 py-2 bg-white/[0.02] border border-white/[0.04] rounded text-sm text-white/70 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/20 hover:text-[#D4AF37] transition-all"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={`flex gap-3 ${msg.role === "user" ? "justify-end" : ""}`}>
            {msg.role === "assistant" && (
              <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                <Bot className="w-3 h-3 text-[#D4AF37]" />
              </div>
            )}
            <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
              msg.role === "user" ? "bg-[#D4AF37] text-black" : "bg-white/[0.04] text-white/90"
            }`}>
              {msg.content}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex gap-3">
            <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <Loader2 className="w-3 h-3 text-[#D4AF37] animate-spin" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="p-3 border-t border-white/[0.04]">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={config.placeholder}
            className="flex-1 bg-white/[0.02] border border-white/[0.04] rounded px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/30"
            disabled={loading}
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            className="p-2 bg-[#D4AF37] rounded hover:bg-[#B8972E] transition-colors disabled:opacity-50"
          >
            <Send className="w-4 h-4 text-black" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default AIAssistant;
